/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";

// Icons
import { IoSend } from "react-icons/io5";

// Mytherra API
import mytherraApi from "services/mytherraApi";

function SendWidget({ balance, onSendSuccess }) {
  const [address, setAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSend = async () => {
    // Reset messages
    setError("");
    setSuccess("");

    // Validation
    if (!address) {
      setError("Please enter a recipient address");
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    if (parseFloat(amount) > balance) {
      setError("Insufficient balance");
      return;
    }

    setSending(true);

    try {
      const txid = await mytherraApi.sendToAddress(address, parseFloat(amount));
      
      if (txid) {
        // Convert txid to string if it's not already
        const txidString = typeof txid === 'string' ? txid : JSON.stringify(txid);
        setSuccess(`Transaction sent successfully! TXID: ${txidString.substring(0, 16)}...`);
        setAddress("");
        setAmount("");
        
        // Callback to refresh parent data
        if (onSendSuccess) {
          setTimeout(onSendSuccess, 1000);
        }
      } else {
        setError("Transaction failed. Please try again.");
      }
    } catch (err) {
      console.error('Send error:', err);
      setError(err.message || "Error sending transaction");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <VuiBox p={3}>
        <VuiBox display="flex" alignItems="center" mb={3}>
          <VuiBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="48px"
            height="48px"
            borderRadius="12px"
            sx={{
              background: "#00CED1",
            }}
          >
            <IoSend size="24px" color="#000000" />
          </VuiBox>
          <VuiBox ml={2}>
            <VuiTypography variant="lg" color="white" fontWeight="bold">
              Send MYT
            </VuiTypography>
            <VuiTypography variant="button" color="text">
              Available: {balance.toFixed(8)} MYT
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        <VuiBox mb={2}>
          <VuiBox mb={1}>
            <VuiTypography variant="caption" color="text" fontWeight="medium">
              Recipient Address
            </VuiTypography>
          </VuiBox>
          <VuiInput
            placeholder="Enter Mytherra address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={sending}
            sx={({ palette: { white } }) => ({
              color: white.main,
            })}
          />
        </VuiBox>

        <VuiBox mb={2}>
          <VuiBox mb={1}>
            <VuiTypography variant="caption" color="text" fontWeight="medium">
              Amount (MYT)
            </VuiTypography>
          </VuiBox>
          <VuiInput
            type="number"
            placeholder="0.00000000"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={sending}
            sx={({ palette: { white } }) => ({
              color: white.main,
            })}
          />
          <VuiBox display="flex" justifyContent="flex-end" mt={1}>
            <VuiTypography
              variant="caption"
              color="info"
              sx={{ cursor: "pointer" }}
              onClick={() => setAmount(balance.toString())}
            >
              Send Max
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        {error && (
          <VuiBox mb={2} p={2} sx={{ background: "rgba(255, 0, 128, 0.1)", borderRadius: "8px" }}>
            <VuiTypography variant="caption" color="error">
              <Icon sx={{ mr: 1, verticalAlign: "middle" }}>error</Icon>
              {error}
            </VuiTypography>
          </VuiBox>
        )}

        {success && (
          <VuiBox mb={2} p={2} sx={{ background: "rgba(0, 206, 209, 0.1)", borderRadius: "8px" }}>
            <VuiTypography variant="caption" color="success">
              <Icon sx={{ mr: 1, verticalAlign: "middle" }}>check_circle</Icon>
              {success}
            </VuiTypography>
          </VuiBox>
        )}

        <VuiButton
          variant="contained"
          color="info"
          fullWidth
          onClick={handleSend}
          disabled={sending}
          sx={{
            background: "linear-gradient(135deg, #0075F6 0%, #4B177E 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #0075F6 0%, #4B177E 100%)",
            },
          }}
        >
          {sending ? (
            <>
              <Icon sx={{ mr: 1 }}>sync</Icon>
              Sending...
            </>
          ) : (
            <>
              <IoSend size="18px" style={{ marginRight: "8px" }} />
              Send Transaction
            </>
          )}
        </VuiButton>

        <VuiBox mt={2}>
          <VuiTypography variant="caption" color="text" textAlign="center" display="block">
            Network Fee: ~0.0001 MYT  •  Estimated Time: ~10 min
          </VuiTypography>
        </VuiBox>
      </VuiBox>
    </Card>
  );
}

export default SendWidget;
